import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Lock, ArrowRight, ArrowLeft, Eye, EyeSlash } from '@phosphor-icons/react';

const ResetPassword = () => {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error('at least 6 characters'); return; }
    if (pw !== pw2) { toast.error('passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/auth/reset', { token, new_password: pw });
      toast.success('Password updated. Sign in with the new one.');
      nav('/login');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'reset failed');
    } finally { setBusy(false); }
  };

  if (!token) return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="glass rounded-2xl p-8 text-center max-w-md">
        <h1 className="font-display text-2xl font-black tracking-tighter">Invalid reset link.</h1>
        <p className="text-white/55 mt-2 text-sm">No token found. Request a new reset email.</p>
        <Link to="/forgot-password" className="mt-5 inline-flex btn-primary px-5 py-3 rounded-full text-sm">
          Get a new link
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/login" data-testid="reset-back" className="inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-white mb-4">
          <ArrowLeft size={14} weight="bold" /> Back to sign in
        </Link>
        <div className="grad-border p-8">
          <h1 className="font-display text-3xl font-black tracking-tighter">Reset password</h1>
          <p className="text-white/55 mt-1 text-sm">Pick something new. Six characters minimum.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">New password</span>
              <div className="mt-1 flex items-center gap-2 px-3 rounded-xl bg-white/5 border border-white/10 focus-within:border-bx-cyan transition">
                <Lock size={16} className="text-white/60" />
                <input data-testid="reset-password" type={show ? 'text' : 'password'} required minLength={6}
                       value={pw} onChange={(e) => setPw(e.target.value)} autoFocus autoComplete="new-password"
                       className="bg-transparent flex-1 py-3 outline-none text-sm" placeholder="••••••••" />
                <button type="button" data-testid="reset-show" onClick={() => setShow((s) => !s)}
                        className="text-white/60 hover:text-white p-1">
                  {show ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">Confirm</span>
              <div className="mt-1 flex items-center gap-2 px-3 rounded-xl bg-white/5 border border-white/10 focus-within:border-bx-cyan transition">
                <Lock size={16} className="text-white/60" />
                <input data-testid="reset-password-confirm" type={show ? 'text' : 'password'} required minLength={6}
                       value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password"
                       className="bg-transparent flex-1 py-3 outline-none text-sm" placeholder="••••••••" />
              </div>
            </label>
            <button data-testid="reset-submit" disabled={busy}
                    className="w-full btn-primary py-3.5 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? 'Updating…' : <>Update password <ArrowRight size={16} weight="bold" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
